import type { IntegrationOp } from '../../../core/domain/integration';

/*
 * The pure logic behind <vitesse-topic-subscription-editor>: validating a
 * hand-added broker topic and folding it into the list.
 *
 * Split out of the component for one reason — the WILDCARD RULE is a security
 * rule, and a security rule that can only be exercised by driving a browser is a
 * security rule nobody re-checks. Here it is a function, so bench/topic-subs.test.ts
 * covers it in CI.
 *
 * The same rule is enforced server-side at Open (driver.BrokerTargets refuses a
 * wildcard target). This is not that check duplicated for safety — the server is
 * the decision, this is the UI declining to send a request it knows will bounce,
 * which is the layered pattern docs/gated-ui.md prescribes.
 */

/** MQTT uses '#' and '+', NATS uses '>' and '*'. A subscription may contain none
 *  of them: unlike a discovery scope, which bounds a sample we let go of in under
 *  a second, a subscription is a STANDING subscription for the life of the
 *  integration — a wildcard one is a permanent tap on the customer's whole broker. */
export const WILDCARD = /[#+>*]/;

export type SubscriptionError = 'empty' | 'wildcard' | null;

/** Validate a proposed subscription. Returns null when it is acceptable. */
export function validateSubscription(name: string, subject: string): SubscriptionError {
  const n = name.trim();
  const s = subject.trim();
  if (!n) return 'empty';
  // Both halves are checked: the subject is what we subscribe to when present, and
  // the NAME is what we subscribe to when it is absent, so a wildcard in either
  // reaches the broker.
  if (WILDCARD.test(n) || WILDCARD.test(s)) return 'wildcard';
  return null;
}

/** Add or replace by name, preserving list order for existing entries so a row
 *  never jumps under the reader when an entry is edited. A blank subject is kept
 *  blank rather than defaulted to the name — the driver already treats an empty
 *  spec as "the name IS the subject", and storing the name twice would make a
 *  later rename silently stop resolving. */
export function upsertSubscription(subs: readonly IntegrationOp[], name: string, subject: string): IntegrationOp[] {
  const n = name.trim();
  const next: IntegrationOp = { name: n, mode: 'readwrite', spec: subject.trim() };
  const at = subs.findIndex((s) => s.name === n);
  if (at < 0) return [...subs, next];
  const copy = subs.slice();
  copy[at] = next;
  return copy;
}

export function removeSubscription(subs: readonly IntegrationOp[], name: string): IntegrationOp[] {
  return subs.filter((s) => s.name !== name);
}
